import React from 'react';
import { Input, Select } from 'antd';
import { SearchOutlined, CloseCircleFilled } from '@ant-design/icons';
import './ndoeRecherche.css';

/**
 * NdoeRecherche — LE bloc de recherche du design system.
 *
 * POURQUOI CE COMPOSANT EXISTE (constat proprio 16/07)
 * La liste des commandes avait DEUX outils au nom presque identique : un bouton
 * « Filtres avancés » (ancien panneau de dates) et un onglet « Recherche
 * avancée ». Le proprio cherchait ses critères dans le premier et ne les
 * trouvait pas. Un seul outil désormais, réutilisable partout (commandes,
 * réceptions…) pour que la recherche s'apprenne UNE seule fois.
 *
 * PRINCIPE : une grande zone de recherche LIBRE qui balaie *tous* les critères
 * à la fois (code, nom, téléphone, livreur, état, produit…), et des filtres
 * facultatifs pour affiner. On tape ce qu'on a en tête ; on affine si besoin.
 *
 * DATES : champs natifs `type="date"` — volontaire. dayjs n'est PAS une
 * dépendance déclarée du projet (il n'arrive que dans les bagages d'antd) :
 * s'appuyer dessus pour un RangePicker serait fragile. Bonus : sur téléphone,
 * le natif ouvre le sélecteur de l'OS, plus familier pour les livreurs.
 *
 * Props :
 *  - q, onQ            : texte libre (obligatoire)
 *  - placeholder       : aide de saisie — ÉNUMÉRER les critères couverts
 *  - periode           : { debut, fin } (optionnel) — plage de dates serveur
 *  - onPeriode         : (debut, fin) => void
 *  - onChercher        : callback du bouton « Chercher » (rechargement serveur)
 *  - chargement        : bool
 *  - facettes          : [{ cle, placeholder, options, valeur, onChange, largeur }]
 *  - champs            : [{ cle, label, type, valeur, onChange, … }] — voir plus bas
 *  - resume            : node — ex. « 12 / 340 commandes »
 *  - onEffacer         : () => void — remet tout à zéro
 *  - actifs            : nombre de filtres actifs (pilote l'affichage d'Effacer)
 *  - children          : contrôles additionnels (ex. une bascule)
 */
/*
 * CHAMPS ÉCLATÉS (31/07/2026) — demande du propriétaire :
 *   « laisser toujours la zone de recherche libre tout en éclatant les champs
 *     pour me permettre de faire des recherches plus ciblées avec une capacité
 *     d'audit ».
 *
 * La zone libre répond à « je cherche quelque chose » ; elle ne répond pas à
 * « montre-moi les ventes de CE livreur, sur CETTE marque, au-dessus de CE
 * montant ». Taper trois mots dans une zone qui balaie tout ne DIT pas quel
 * critère porte quoi — et un audit a besoin de savoir exactement ce qu'il a
 * demandé pour pouvoir défendre le chiffre obtenu.
 *
 * Les champs sont donc VISIBLES en permanence, chacun avec son étiquette. Pas
 * de panneau « avancé » à déplier : c'est précisément ce motif — des critères
 * cachés derrière un bouton — qui avait égaré le propriétaire sur la liste des
 * commandes, et la raison d'être de ce composant (voir en-tête).
 *
 * Forme d'un champ :
 *   { cle, label, type: 'texte' | 'nombre' | 'liste',
 *     valeur, onChange, placeholder, options, suffixe, largeur }
 */
export default function NdoeRecherche({
  q, onQ,
  placeholder = 'Rechercher…',
  periode, onPeriode, onChercher, chargement = false,
  facettes = [],
  champs = [],
  resume,
  onEffacer,
  actifs = 0,
  children,
}) {
  const aPeriode = !!(periode && onPeriode);

  return (
    <section className="ndoe-rech" role="search">
      {/* Ligne 1 — la recherche libre : volontairement DOMINANTE, c'est le
          geste principal. Les filtres ne sont qu'un affinage. */}
      <div className="ndoe-rech__ligne ndoe-rech__ligne--principale">
        <Input
          className="ndoe-rech__q"
          value={q}
          onChange={(e) => onQ(e.target.value)}
          allowClear
          size="large"
          prefix={<SearchOutlined className="ndoe-rech__loupe" />}
          placeholder={placeholder}
        />
        {aPeriode && (
          <div className="ndoe-rech__periode">
            <label className="ndoe-rech__date">
              <span>Du</span>
              <input type="date" value={periode.debut} onChange={(e) => onPeriode(e.target.value, periode.fin)} />
            </label>
            <label className="ndoe-rech__date">
              <span>au</span>
              <input type="date" value={periode.fin} onChange={(e) => onPeriode(periode.debut, e.target.value)} />
            </label>
            <button type="button" className="ndoe-rech__go" onClick={onChercher} disabled={chargement}>
              <SearchOutlined /> {chargement ? 'Recherche…' : 'Chercher'}
            </button>
          </div>
        )}
      </div>

      {/* Ligne 2 — affinage. Masquée s'il n'y a ni facette ni contrôle. */}
      {(facettes.length > 0 || children) && (
        <div className="ndoe-rech__ligne ndoe-rech__ligne--facettes">
          {facettes.map((f) => (
            <Select
              key={f.cle}
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder={f.placeholder}
              value={f.valeur}
              onChange={f.onChange}
              options={f.options}
              className="ndoe-rech__facette"
              style={{ minWidth: f.largeur || 150 }}
            />
          ))}
          {children}
          {actifs > 0 && onEffacer && (
            <button type="button" className="ndoe-rech__effacer" onClick={onEffacer}>
              <CloseCircleFilled /> Effacer {actifs > 1 ? `les ${actifs} filtres` : 'le filtre'}
            </button>
          )}
        </div>
      )}

      {/* Ligne 3 — recherche CIBLÉE, champ par champ. Chaque critère porte son
          étiquette : on voit ce qu'on a demandé, donc on peut le justifier. */}
      {champs.length > 0 && (
        <div className="ndoe-rech__champs">
          {champs.map((c) => (
            <label key={c.cle} className="ndoe-rech__champ" style={c.largeur ? { flexBasis: c.largeur } : undefined}>
              <span className="ndoe-rech__champ-label">{c.label}</span>
              {c.type === 'liste' ? (
                <Select
                  allowClear showSearch optionFilterProp="label"
                  placeholder={c.placeholder || 'Tous'}
                  value={c.valeur ?? undefined}
                  onChange={c.onChange}
                  options={c.options || []}
                  size="small"
                  className="ndoe-rech__champ-liste"
                />
              ) : (
                <Input
                  size="small"
                  allowClear
                  type={c.type === 'nombre' ? 'number' : 'text'}
                  inputMode={c.type === 'nombre' ? 'decimal' : undefined}
                  value={c.valeur ?? ''}
                  onChange={(e) => c.onChange(e.target.value)}
                  placeholder={c.placeholder}
                  suffix={c.suffixe ? <span className="ndoe-rech__champ-suffixe">{c.suffixe}</span> : undefined}
                />
              )}
            </label>
          ))}
        </div>
      )}

      {resume && <div className="ndoe-rech__resume">{resume}</div>}
    </section>
  );
}
